/**
 * @author       kevin.chau@zumper.com (Kevin Chau)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  index.js
 */

import { createSelector } from '@reduxjs/toolkit'
import { selectCurrentCity, selectCurrentHood } from '@zcode-ui/locale-guide'
import { compose } from 'redux'

export {
  selectCurrentCity,
  selectCurrentMinCities,
  selectCurrentMinCity,
  selectCurrentHood,
} from '@zcode-ui/locale-guide'

export * from './cityGuideCity'
export * from './cityGuideNeighborhood'

/**
 *
 * @param {import('@zcode/state-type').State} state
 */
export const selectCurrentSearch = (state) => state.currentSearch

export const selectCurrentSearchHoodOrCityUrl = compose(
  (currentSearch) => currentSearch.hood?.path || currentSearch.city?.path,
  selectCurrentSearch
)

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {number | null} buildingId
 */
export const selectAdditionalBuildingData = (state, buildingId) => {
  if (!buildingId) {
    return null
  }
  const currentSearch = selectCurrentSearch(state)
  if (!currentSearch) {
    return null
  }
  return currentSearch.additionalBuildingData[buildingId] || null
}

export const selectCurrentCityInventoryCounts = compose(
  (state) => state?.inventoryCounts,
  selectCurrentCity
)

export const selectCurrentHoodPrices = compose((state) => state?.pricing, selectCurrentHood)

const selectLastCurrentHoodMaxMinPrices = compose(
  (state) => state && state.length && state[state.length - 1],
  selectCurrentHoodPrices
)

export const selectCurrentHoodMaxPrice = compose(
  (state) => state && state.pricePercentileMax,
  selectLastCurrentHoodMaxMinPrices
)

export const selectCurrentHoodMinPrice = compose(
  (state) => state && state.pricePercentileMin,
  selectLastCurrentHoodMaxMinPrices
)

export const selectCurrentCityPrices = compose((state) => state && state.pricing, selectCurrentCity)

const selectLastCurrentCityMaxMinPrices = compose(
  (state) => state && state.length && state[state.length - 1],
  selectCurrentCityPrices
)

export const selectCurrentCityMaxPrice = compose(
  (state) => state && state.pricePercentileMax,
  selectLastCurrentCityMaxMinPrices
)

export const selectCurrentCityMinPrice = compose(
  (state) => state && state.pricePercentileMin,
  selectLastCurrentCityMaxMinPrices
)

export const selectCurrentHoodCountry = compose(
  (state) => state && state.country,
  selectCurrentHood
)
export const selectCurrentHoodState = compose((state) => state && state.state, selectCurrentHood)

export const selectCurrentCityCountry = compose(
  (state) => state && state.country,
  selectCurrentCity
)

export const selectCurrentCityState = compose((state) => state && state.state, selectCurrentCity)

export const selectHasMoreListables = compose(
  (currentSearch) => currentSearch && currentSearch.hasMoreListables,
  selectCurrentSearch
)

export const selectListables = compose(
  (currentSearch) => currentSearch?.listables,
  selectCurrentSearch
)

export const selectListablesQuery = compose((listables) => listables?.query, selectListables)

export const selectQuerySort = compose((query) => query?.sort, selectListablesQuery)

export const selectCurrentSearchLocation = compose(
  (currentSearch) => currentSearch?.location,
  selectCurrentSearch
)

export const selectCurrentSearchCityName = compose(
  (currentSearch) => currentSearch?.cityName ?? '',
  selectCurrentSearchLocation
)

export const selectCurrentSearchStateName = compose(
  (currentSearch) => currentSearch?.stateName ?? '',
  selectCurrentSearchLocation
)

export const selectCurrentSearchUrl = compose(
  (location) => location?.url,
  selectCurrentSearchLocation
)

export const selectCurrentHoodType = compose((hood) => hood?.type, selectCurrentHood)

export const selectIsFromPaidMarketing = compose(
  (currentSearch) => currentSearch?.isFromPaidMarketing ?? false,
  selectCurrentSearch
)

export const selectFirstPageAdditionalCount = compose(
  (currentSearch) => currentSearch?.firstPageAdditionalCount ?? null,
  selectCurrentSearch
)

export const selectFirstPageCount = compose(
  (currentSearch) => currentSearch?.firstPageCount ?? null,
  selectCurrentSearch
)

export const selectFirstPageOffset = compose(
  (currentSearch) => currentSearch?.firstPageOffset ?? null,
  selectCurrentSearch
)

export const selectPaginatedIds = compose(
  (currentSearch) => currentSearch?.paginatedIds ?? [],
  selectCurrentSearch
)

export const selectCurrentSearchRoutePathname = compose(
  (currentSearch) => currentSearch.pathname,
  selectCurrentSearch
)

export const selectCurrentSearchRouteSearchString = compose(
  (currentSearch) => currentSearch.searchString,
  selectCurrentSearch
)

export const selectCurrentSearchLastClickedEntity = compose(
  (currentSearch) => currentSearch.lastClickedEntity,
  selectCurrentSearch
)

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} entity
 * @returns
 */
export const selectWasCurrentEntityOnceAFilterAd = (state, entity) => {
  if (!entity?.buildingId && !entity?.listingId) {
    return false
  }

  const lastClickedEntity = selectCurrentSearchLastClickedEntity(state)
  const isFilterAd =
    lastClickedEntity.isFilterAd &&
    ((entity.buildingId && lastClickedEntity.buildingId === entity.buildingId) ||
      (entity.listingId && lastClickedEntity.listingId === entity.listingId))

  return isFilterAd
}

export const selectIsNavbarExpanded = compose(
  (currentSearch) => currentSearch.isNavbarExpanded,
  selectCurrentSearch
)

export const selectMapListPageLastUpdated = createSelector(
  selectCurrentSearch,
  selectListables,
  ({ city }, { listables, nearby }) => {
    const timestamps = listables.map(({ modifiedOn }) => modifiedOn).filter(Boolean)
    if (nearby?.matches) {
      for (const listable of nearby.matches.listables) {
        if (listable.modifiedOn) {
          timestamps.push(listable.modifiedOn)
        }
      }
    }
    if (city) {
      timestamps.push(city.modifiedOn)
    }
    if (!timestamps.length) {
      return null
    }
    return Math.max(...timestamps)
  }
)
